import React, { useCallback, useState } from 'react';
import { FAST_API_BASE_PEOPLE } from './config';

export function usePeopleApi(accessToken) {

  const [people, setPeople] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [creatingProfile, setCreatingProfile] = useState({});
  const [total, setTotal] = useState(0);
  const [forceUpdate, setForceUpdate] = useState(0);
  // Ref to always access latest people array
  const peopleRef = React.useRef(people);
  React.useEffect(() => {
    peopleRef.current = people;
  }, [people]);
  const [profileLinks, setProfileLinks] = useState({});
  const [exportLinks, setExportLinks] = useState({});
  const [exporting, setExporting] = useState({});
  const [lastSynced, setLastSynced] = useState(null);
  const [actionErrors, setActionErrors] = useState({});

  const fetchPeople = useCallback(async (filters = '') => {
    setLoading(true);
    setError(null);
    const params = typeof filters === 'string' ? { name: filters } : filters || {};
    const trimmedQuery = params?.name?.trim?.() ?? '';
    const skillLevels = Array.isArray(params?.skillLevels)
      ? params.skillLevels
      : [];
    const sources = Array.isArray(params?.sources)
      ? params.sources.filter((source) => typeof source === 'string' && source.trim().length > 0)
      : [];
    const modifiedAfter = params?.modifiedAfter ?? null;
    const limit = typeof params?.limit === 'number' ? Math.max(1, Math.min(params.limit, 200)) : undefined;
    const offset = typeof params?.offset === 'number' ? Math.max(0, params.offset) : undefined;

    if (!accessToken) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[usePeopleApi] fetchPeople called without access token');
      }
      setLoading(false);
      setError('Access token unavailable');
      setTotal(0);
      return;
    }

    try {
       const url = new URL(FAST_API_BASE_PEOPLE);
      if (trimmedQuery.length > 0) {
        url.searchParams.set('name', trimmedQuery);
      }
       skillLevels.forEach(({ id, level }) => {
         url.searchParams.set(`skilllevels[${id}]`, level == null ? '' : String(level));
       });
       if (sources.length > 0) {
         url.searchParams.set('source', sources.join(','));
       }
       if (modifiedAfter) {
         url.searchParams.set('modifiedafter', modifiedAfter);
       }
       if (typeof limit === 'number') {
         url.searchParams.set('limit', String(limit));
       }
       if (typeof offset === 'number') {
         url.searchParams.set('offset', String(offset));
       }

       const response = await fetch(url.toString(), {
         headers: {
            Authorization: `Bearer ${accessToken}`
         }
      });

      if (!response.ok) {
        let message = 'Failed to fetch people';
        try {
          const errBody = await response.json();
          if (typeof errBody?.detail === 'string') {
            message = errBody.detail;
          } else if (Array.isArray(errBody?.detail) && errBody.detail[0]?.msg) {
            message = errBody.detail[0].msg;
          }
        } catch (_) {
          // ignore JSON parsing issues
        }
        throw new Error(message);
      }

       const data = await response.json();
       const results = Array.isArray(data?.results) ? data.results : [];
       const totalCount = typeof data?.total === 'number' ? data.total : results.length;

       const normalizedResults = results.map((person) => ({
         ...person,
         description: person?.description ?? null,
         tt_id: person?.tt_id ?? null,
        companies: person?.companies ?? null,
        skills: person?.skills ?? []
       }));

       setPeople(normalizedResults);
       setTotal(totalCount);
       setLastSynced(new Date().toISOString());
       setError(null);
     } catch (err) {
       setError(err.message || 'Failed to fetch people');
       setPeople([]);
       setTotal(0);
     } finally {
       setLoading(false);
     }
  }, [accessToken]);

  const deletePerson = useCallback(async (personid) => {
    if (!accessToken) {
      throw new Error('Access token unavailable');
    }
    const response = await fetch(`${FAST_API_BASE_PEOPLE}/${personid}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${accessToken}` }
    });
    if (!response.ok) {
      let message = 'Failed to delete person';
      try {
        const body = await response.json();
        if (typeof body?.detail === 'string') message = body.detail;
      } catch (_) {
        // Keep the fallback message when the response has no JSON body.
      }
      throw new Error(message);
    }
  }, [accessToken]);

  // Check if a person has a profile
  const hasProfile = async (personid) => {
    if (!accessToken) return false;

    try {
       const response = await fetch(`${FAST_API_BASE_PEOPLE}/${personid}`, {
         headers: { Authorization: `Bearer ${accessToken}` }
       });
       if (!response.ok) throw new Error('Failed to fetch person');
       const data = await response.json();
       const description = typeof data?.description === 'string' ? data.description.trim() : '';
       const hasDesc = description.length > 0;
       return hasDesc;
     } catch (error) {
       return false;
     }
   };

  // Create a profile and poll for readiness
  // Compares two person objects for changes (deep comparison on relevant fields)
  const person_has_changed = async (currentPerson, personid) => {
    if (!accessToken) return false;

    try {
       const response = await fetch(`${FAST_API_BASE_PEOPLE}/${personid}`, {
          headers: { Authorization: `Bearer ${accessToken}` }
       });
       if (response.ok) {
         const data = await response.json();
         const personData = data;
         if (!personData) return false;
        // Compare all keys in currentPerson to personData (shallow, or customize as needed)
        // You may want to compare only certain fields, e.g. description, name, etc.
        // Here, compare JSON stringified objects for simplicity
        return JSON.stringify(currentPerson) !== JSON.stringify(personData);
      }
    } catch (e) {
      // ignore
    }
    return false;
  };



  // Pass the current person object as a parameter!
  const triggerMakeProfile = async (personid, roleDetails) => {
    const trimmedRoleDetails = typeof roleDetails === 'string' ? roleDetails.trim() : '';
    const requestOptions = {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`
      }
    };

    if (trimmedRoleDetails) {
      requestOptions.headers['Content-Type'] = 'application/json';
      requestOptions.body = JSON.stringify({ roledetails: trimmedRoleDetails });
    }

    const response = await fetch(`${FAST_API_BASE_PEOPLE}/${personid}/actions/makeprofile`, requestOptions);

    if (!response.ok) {
      let message = 'Failed to trigger profile creation';
      try {
        const errBody = await response.json();
        if (typeof errBody?.detail === 'string') {
          message = errBody.detail;
        } else if (Array.isArray(errBody?.detail) && errBody.detail[0]?.msg) {
          message = errBody.detail[0].msg;
        }
      } catch (_) {
        // ignore JSON parsing issues
      }
      throw new Error(message);
    }
  };

  const handleCreateProfile = async (personid, currentPerson, options = {}) => {
    const resetState = (message, stage = 'unknown') => {
      setCreatingProfile(prev => ({ ...prev, [personid]: false }));
      setProfileLinks(prev => {
        const { [personid]: _, ...rest } = prev;
        return rest;
      });
      if (message) {
        if (process.env.NODE_ENV === 'development') {
          console.warn('[usePeopleApi] handleCreateProfile error', {
            personid,
            stage,
            message,
            peopleCount: peopleRef.current.length
          });
        }
        setActionErrors(prev => ({
          ...prev,
          [personid]: { message, stage }
        }));
      }
    };

    setCreatingProfile(prev => ({ ...prev, [personid]: true }));
    setProfileLinks(prev => ({ ...prev, [personid]: "CREATING_PROFILE" }));
    setActionErrors(prev => {
      const next = { ...prev };
      delete next[personid];
      return next;
    });

    if (!accessToken) {
      resetState('Access token unavailable', 'preflight');
      return;
    }

    try {
      await triggerMakeProfile(personid, options?.roleDetails);
    } catch (error) {
      resetState(error.message || 'Failed to trigger profile creation', 'triggerMakeProfile');
      return;
    }

    // Poll for changes using person_has_changed
    const poll = async () => {
      const changed = await person_has_changed(currentPerson, personid);
      if (changed) {
        // Fetch the latest person data and update state
        try {
           const response = await fetch(`${FAST_API_BASE_PEOPLE}/${personid}`, {
              headers: { Authorization: `Bearer ${accessToken}` }
           });
           if (response.ok) {
             const data = await response.json();
             const personData = data;
            if (personData) {
              setPeople(prev => {
                const updated = prev.map(p => {
                  const pId = String(p.personid);
                  const dId = String(personData.personid);
                  if (pId === dId) {
                    return { ...p, ...personData };
                  } else {
                    return p;
                  }
                });
                return updated;
              });
              setCreatingProfile(prev => ({ ...prev, [personid]: false }));
              setProfileLinks(prev => ({
                ...prev,
                [personid]: `/HoggedIn?personid=${personid}`
              }));
                setActionErrors(prev => {
                  const next = { ...prev };
                  delete next[personid];
                  return next;
                });
                setForceUpdate(v => v + 1); // Force re-render
                if (typeof options?.onProfileReady === 'function') {
                  options.onProfileReady(personData);
                }
                return; // Exit polling
              }
            }
          } catch (e) {
          if (process.env.NODE_ENV === 'development') {
            console.warn('[usePeopleApi] poll fetch failed, retrying', {
              personid,
              error: e?.message
            });
          }
        }
      } else {
        setTimeout(poll, 3000);
      }
    };
    poll();
  };

  // Export profile
  const handleExportProfile = async (personid, presentationId) => {
    setExporting(prev => ({ ...prev, [personid]: true }));
    setActionErrors(prev => {
      const next = { ...prev };
      delete next[personid];
      return next;
    });

    if (!accessToken) {
      setActionErrors(prev => ({
        ...prev,
        [personid]: { message: 'Access token unavailable', stage: 'export-preflight' }
      }));
      setExporting(prev => ({ ...prev, [personid]: false }));
      return;
    }

    try {
      const response = await fetch(`${FAST_API_BASE_PEOPLE}/${personid}/actions/exportprofile`, {
        method: 'POST',
        headers: {
          Authorization: `Bearer ${accessToken}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          presentation_id: presentationId,
        }),
      });
      if (!response.ok) {
        let message = 'Export failed';
        try {
          const errBody = await response.json();
          if (typeof errBody?.detail === 'string') {
            message = errBody.detail;
          } else if (Array.isArray(errBody?.detail) && errBody.detail[0]?.msg) {
            message = errBody.detail[0].msg;
          }
        } catch (_) {
          // ignore
        }
        throw new Error(message);
      }
      const data = await response.json();
      const docId = data.new_presentation_id;
      if (docId) {
        const docUrl = `https://docs.google.com/presentation/d/${docId}`;
        setExportLinks(prev => ({ ...prev, [personid]: docUrl }));
        setActionErrors(prev => {
          const next = { ...prev };
          delete next[personid];
          return next;
        });
      }
    } catch (error) {
      if (process.env.NODE_ENV === 'development') {
        console.warn('[usePeopleApi] handleExportProfile error', {
          personid,
          message: error?.message
        });
      }
      setActionErrors(prev => ({
        ...prev,
        [personid]: { message: error?.message || 'Export failed', stage: 'export' }
      }));
    } finally {
      setExporting(prev => ({ ...prev, [personid]: false }));
    }
  };

  return {
    people,
    loading,
    error,
    creatingProfile,
    total,
    profileLinks,
    exportLinks,
    exporting,
    fetchPeople,
    deletePerson,
    handleCreateProfile,
    handleExportProfile,
    hasProfile,
    person_has_changed,
    forceUpdate,
    setForceUpdate,
    lastSynced,
    setPeople,
    setProfileLinks,
    setExportLinks,
    setError,
    actionErrors
  };
}
